import { css, CSSProps } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

import { BgColors } from "../../../components/wpl/link/src/index";

import { LinkListContainer } from "./global-footer-top-link-list-container";
import { LogoLinkContainer } from "./global-footer-top-logo";

// ==================================================================
// CSS styling
// ==================================================================
const containerStyle = css({
    padding: "0 0 32px 0",
    position: "relative",
    marginInlineEnd: "auto",
    marginInlineStart: "auto",
    minWidth: "100%",
    clear: "both",
    border: "0",
    fontSize: "100%",
    verticalAlign: "baseline"
});

const rowStyle = css({
    marginInlineStart: "auto",
    marginInlineEnd: "auto",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    alignContent: "flex-start",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gridTemplateAreas: '"logo logo""links links"',
    gap: "32px",
    width: "100%",

    [media.only.md]: {
        gridTemplateColumns: "repeat(4, minmax(0,1fr))",
        gridTemplateAreas: '"logo links links links"'
    },

    [media.above.md]: {
        gridTemplateColumns: "minmax(0, 18%) repeat(4, minmax(0,1fr))",
        gridTemplateAreas: '"logo links links links links"'
    }
});

const linksWrapperStyle = css({
    gridArea: "links",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "32px",

    [media.above.sm]: {
        gridTemplateColumns: "repeat(4, minmax(0,1fr))"
    }
});

export const ComponentStyleProps: CSSProps<unknown> = {
    margin: "0",
    padding: "0",
    border: "0",
    fontSize: "100%",
    verticalAlign: "baseline"
};

// ==================================================================
// Rendering Functions
// ==================================================================
export const GlobalFooterTop = () => {
    const intl = useIntl();
    const productsLinkList = {
        headerText: intl.formatMessage({
            id: "globalFooter.top.products",
            defaultMessage: "Products"
        }),
        listOfLinks: [
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.jira-software",
                    defaultMessage: "Jira Software"
                }),
                href: useLocalizedHref("/software/jira")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.jira-align",
                    defaultMessage: "Jira Align"
                }),
                href: useLocalizedHref("/software/jira/align")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.jira-service-management",
                    defaultMessage: "Jira Service Management"
                }),
                href: useLocalizedHref("/software/jira/service-management")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.jira-work-management",
                    defaultMessage: "Jira Work Management"
                }),
                href: useLocalizedHref("/software/jira/work-management")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.jira-product-discovery",
                    defaultMessage: "Jira Product Discovery"
                }),
                href: useLocalizedHref("/software/jira/product-discovery")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.confluence",
                    defaultMessage: "Confluence"
                }),
                href: useLocalizedHref("/software/confluence")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.trello",
                    defaultMessage: "Trello"
                }),
                href: "https://trello.com/home"
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.bitbucket",
                    defaultMessage: "Bitbucket"
                }),
                href: useLocalizedHref("/software/bitbucket")
            }
        ],
        bottomViewAllLink: {
            text: intl.formatMessage({
                id: "globalFooter.top.view-all-products",
                defaultMessage: "View all products"
            }),
            href: useLocalizedHref("/software"),
            bgColor: "bgWhite" as BgColors
        }
    };
    const resourcesLinkList = {
        headerText: intl.formatMessage({
            id: "globalFooter.top.resources",
            defaultMessage: "Resources"
        }),
        listOfLinks: [
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.technical-support",
                    defaultMessage: "Technical Support"
                }),
                href: "https://support.atlassian.com/"
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.purchasing-and-licensing",
                    defaultMessage: "Purchasing & licensing"
                }),
                href: useLocalizedHref("/licensing/purchase-licensing")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.atlassian-community",
                    defaultMessage: "Atlassian Community"
                }),
                href: "https://community.atlassian.com/"
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.knowledge-base",
                    defaultMessage: "Knowledge base"
                }),
                href: "https://confluence.atlassian.com/kb"
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.marketplace",
                    defaultMessage: "Marketplace"
                }),
                href: "https://marketplace.atlassian.com/"
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.my-account",
                    defaultMessage: "My Account"
                }),
                href: "https://my.atlassian.com/products/index"
            }
        ],
        bottomViewAllLink: {
            text: intl.formatMessage({
                id: "globalFooter.top.create-support-ticket",
                defaultMessage: "Create support ticket"
            }),
            href: "https://support.atlassian.com/contact/",
            bgColor: "bgWhite" as BgColors
        }
    };
    const expandAndLearnLinkList = {
        headerText: intl.formatMessage({
            id: "globalFooter.top.expand-and-learn",
            defaultMessage: "Expand & Learn"
        }),
        listOfLinks: [
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.partners",
                    defaultMessage: "Partners"
                }),
                href: useLocalizedHref("/partners")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.training-and-certification",
                    defaultMessage: "Training & Certification"
                }),
                href: useLocalizedHref("/university")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.documentation",
                    defaultMessage: "Documentation"
                }),
                href: "https://confluence.atlassian.com/display/ALLDOC/Atlassian+Documentation"
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.developer-resources",
                    defaultMessage: "Developer Resources"
                }),
                href: useLocalizedHref("/developers")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.enterprise-services",
                    defaultMessage: "Enterprise services"
                }),
                href: useLocalizedHref("/enterprise/services")
            }
        ],
        bottomViewAllLink: {
            text: intl.formatMessage({
                id: "globalFooter.top.view-all-resources",
                defaultMessage: "View all resources"
            }),
            href: useLocalizedHref("/resources"),
            bgColor: "bgWhite" as BgColors
        }
    };
    const aboutAtlassianLinkList = {
        headerText: intl.formatMessage({
            id: "globalFooter.top.about-atlassian",
            defaultMessage: "About Atlassian"
        }),
        listOfLinks: [
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.company",
                    defaultMessage: "Company"
                }),
                href: useLocalizedHref("/company")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.careers",
                    defaultMessage: "Careers"
                }),
                href: useLocalizedHref("/company/careers")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.events",
                    defaultMessage: "Events"
                }),
                href: useLocalizedHref("/company/events")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.blogs",
                    defaultMessage: "Blogs"
                }),
                href: useLocalizedHref("/blog")
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.atlassian-foundation",
                    defaultMessage: "Atlassian Foundation"
                }),
                href: "https://www.atlassianfoundation.org/"
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.investor-relations",
                    defaultMessage: "Investor Relations"
                }),
                href: "https://investors.atlassian.com"
            },
            {
                text: intl.formatMessage({
                    id: "globalFooter.top.trust-and-security",
                    defaultMessage: "Trust & Security"
                }),
                href: useLocalizedHref("/trust")
            }
        ],
        bottomViewAllLink: {
            text: intl.formatMessage({
                id: "globalFooter.top.contact-us",
                defaultMessage: "Contact us"
            }),
            href: useLocalizedHref("/company/contact"),
            bgColor: "bgWhite" as BgColors
        }
    };

    return (
        <div css={containerStyle}>
            <Row>
                <div css={rowStyle}>
                    <div css={css({ gridArea: "logo" })}>
                        <LogoLinkContainer />
                    </div>
                    <div css={linksWrapperStyle}>
                        <LinkListContainer {...productsLinkList} />
                        <LinkListContainer {...resourcesLinkList} />
                        <LinkListContainer {...expandAndLearnLinkList} />
                        <LinkListContainer {...aboutAtlassianLinkList} />
                    </div>
                </div>
            </Row>
        </div>
    );
};
